/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component,  computed,  CUSTOM_ELEMENTS_SCHEMA,  effect,  inject,  signal } from '@angular/core';
import { MarkdownModule } from 'ngx-markdown';
import { Message } from 'src/chatbot/interface/message.interface';
import { dataChatService } from 'src/chatbot/service/dataChat.service';
import { ClipBoardIconComponent } from 'src/chatbot/icons/clipboard-icon.component';
import { SoundIconComponent } from 'src/chatbot/icons/sound-icon.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StopMusicIconComponent } from 'src/chatbot/icons/stop-icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { DebugService } from 'src/app/services/debug.service';
import { AudioStorageService } from 'src/chatbot/service/audio-storage.service';
import { LinkInterceptorDirective } from 'src/app/directives/link/link.directive';
import { PlayAudioHandlerService } from 'src/chatbot/service/playAudioHandler.service';
import { environment } from 'src/environments/environment';
import { RestService } from 'src/app/services';

@Component({
  selector: 'chat-bubble',
  standalone: true,
  imports: [
    CommonModule,
    MarkdownModule,
    ClipBoardIconComponent,
    SoundIconComponent,
    MatButtonModule, 
    LinkInterceptorDirective,
    MatTooltipModule,
    StopMusicIconComponent,
    TranslateModule
  ],
  templateUrl:'./chatBubble.component.html',
  styleUrls: ['./chatBubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatBubbleComponent {
  private rest = inject(RestService);
  private debugService = inject(DebugService);
  private playAudioHandlerService = inject(PlayAudioHandlerService);
  private audioStorageService = inject(AudioStorageService);
  public dataChat = inject(dataChatService);

  public isStreaming = computed(() => this.dataChat.isStreaming())
  public isPlayingOnDemand = computed(() => this.playAudioHandlerService.isPlayingOnDemand());
  public isLoadingAudioById = signal(null);

  public messages = signal<Message[]>([]);
  private timeout: any;

  public e = effect(() => {
    if(this.dataChat.requestIsFulliled()){
      this.messages.set(this.dataChat.messages())
    }
  }, {allowSignalWrites: true})

  isUser(role: string) {
    return role === 'user';
  }

  copyToClipboard(event: any){
    const text = event.target.closest('.chat__message-wrapper')?.querySelector('.markdown').innerText;
    if (!text) 
      return;

    const tooltil = event.target.closest('.action-icons-container')?.querySelector('.copy-text');
    if (tooltil){
      tooltil.classList.add('tooptip-show');
      this.timeout = setTimeout(() => {
        tooltil.classList.remove('tooptip-show');
      }, 1000);
    }

    navigator.clipboard.writeText(text).catch((err) => {
      this.debugService.error(err, '[ChatBubbleComponent] Error copying text');
    });
  }

  removeEmojis(text: string): string {
    return text.replace(
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
      ''
    );
  }

  async playAudio(event, errAudioElm){
    const target = event.currentTarget as HTMLElement; 
    const completion_id = target.getAttribute('data-tracking-id'); 
    const audioBlob = await this.audioStorageService.getAudio(completion_id);
    if (!audioBlob){
      const text = event.target.closest('.chat__message-wrapper')?.querySelector('.markdown').innerText;
      const textWithoutEmojis = this.removeEmojis(text)?.replaceAll('-', ' ');
      const audioObject = { id: completion_id, text: textWithoutEmojis };
      this.getAudioFromText(audioObject, errAudioElm);
      return;
    }

    const audioObject = { completion_id, audioBlob };
    this.playAudioHandlerService.playAudioOnDemand(audioObject);
  }


  stopAudio(){
    this.playAudioHandlerService.stopAudioOnDemand();
  }

  checkPlayingOnDemand(id){
    return this.isPlayingOnDemand() === id;
  }

  getAudioFromText(audioObject: any, evt: any) {
    const { id, text } = audioObject;
    if (!text || text === ""){
      console.warn("No hay texto para reproducir");
      return;
    }

    this.playFromAPI(id, text, evt);
  }

  async playFromAPI(id: any, text: string, evt: any) {
    this.isLoadingAudioById.set(id);
    const apiUrl = environment.urlTextToSpeech;

    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ service: this.rest.device?.chatBotId ?? 0, text })
      });

      if (!res.ok) {
        this.showErrorAudioTooltip(evt);
        const errText = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${res.statusText} ${errText}`.trim());
      }

      if (!res.body){
        this.showErrorAudioTooltip(evt);
        throw new Error('La respuesta no trae body (stream).');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) 
            break;

          buffer += decoder.decode(value, { stream: true });
        }
      } finally {
        reader.releaseLock();
      }

      const blob = this.playAudioHandlerService.getAudioBlobFromChunks([buffer]);
      await this.audioStorageService.storeAudio(id, blob);

      this.playAudioHandlerService.playAudioOnDemand({
        completion_id: id,
        audioBlob: blob,
      });

    } catch (err) {
      this.showErrorAudioTooltip(evt);
      console.error('playFromAPI error:', err);
      throw err;
    } finally {
      this.isLoadingAudioById.set(null);
    }
  }

  private showErrorAudioTooltip(target: any){
    const tooltil = target.closest('.action-icons-container')?.querySelector('.error-audio');
    if (tooltil){
      tooltil.classList.add('tooptip-show');
      this.timeout = setTimeout(() => {
        tooltil.classList.remove('tooptip-show');
      }, 1000);
    }
  }
}
